import React, { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, Home, Plus, Edit3, Trash2, MapPin, Clock, X, ChevronDown, Camera, Image as ImageIcon, AlertCircle, CheckCircle, XCircle, Search, User, Phone, FileText, StickyNote, Wifi, WifiOff, RefreshCw, CloudOff } from 'lucide-react';
import type { CurrentPage } from '../App';
import { 
  fetchAppointments, 
  fetchAppointmentTypes, 
  createAppointment, 
  updateAppointment, 
  deleteAppointment,
  addAppointmentType,
  deleteAppointmentType,
  uploadAppointmentPhoto,
  getAppointmentPhotoUrl,
  deleteAppointmentPhoto,
  initializeDefaultAppointmentTypes,
  fetchAppointmentNotes,
  createAppointmentNote,
  deleteAppointmentNote,
  type Appointment,
  type AppointmentType,
  type AppointmentStatus,
  type AppointmentNote
} from '../services/appointmentService';
import { getOrganizationMembers, getUserOrganization } from '../services/teamService';
import type { OrganizationMember } from '../types/team';
import { searchAddressesByPostcode, formatAddressForDisplay, type UKAddress } from '../services/ukAddressService';
import { supabase } from '../lib/supabaseClient';
import CalendarSyncService from '../services/calendarSyncService';
import type { SyncStatus } from '../services/calendarSyncService';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

interface CalendarPageProps {
  onNavigate: (page: CurrentPage) => void;
}

interface NewAppointment {
  appointment_type: string;
  date: string;
  time: string;
  address: string;
  client_name: string;
  phone_number: string;
  status: AppointmentStatus;
  assigned_to_user_id?: string;
  photo_url?: string;
}

const CalendarPage: React.FC<CalendarPageProps> = ({ onNavigate }) => {
  // Scroll to top when page loads (backup - App.tsx also handles this)
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Helper to format time (remove seconds)
  const formatTime = (timeString: string): string => {
    if (!timeString) return '';
    const parts = timeString.split(':');
    return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : timeString;
  };

  // Helper to format team member name (firstName + lastInitial, max 8 chars)
  const formatMemberName = (member: OrganizationMember): string => {
    // Try to get name from metadata or email
    const firstName = member.user_metadata?.first_name || member.email?.split('@')[0] || '';
    const lastName = member.user_metadata?.last_name || '';
    const lastInitial = lastName.charAt(0);
    
    // If no last initial, just use first name
    if (!lastInitial) {
      return firstName.length > 8 ? firstName.substring(0, 8) + '...' : firstName || member.user_id.substring(0, 8);
    }
    
    // Build name with last initial: "FirstName L"
    const fullDisplay = `${firstName} ${lastInitial}`;
    
    // If total length exceeds 8 chars, truncate first name but keep last initial
    if (fullDisplay.length > 8) {
      // Calculate how much space we have for first name (8 - space - initial = 6 chars)
      const maxFirstNameLength = 6;
      const truncatedFirstName = firstName.substring(0, maxFirstNameLength);
      return `${truncatedFirstName}... ${lastInitial}`;
    }
    
    return fullDisplay;
  };

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDirectionsModal, setShowDirectionsModal] = useState(false);
  const [directionsAddress, setDirectionsAddress] = useState<string>('');
  const [viewingAppointment, setViewingAppointment] = useState<Appointment | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddTypeInput, setShowAddTypeInput] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Address search state
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [addressResults, setAddressResults] = useState<UKAddress[]>([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  
  // Notes state
  const [appointmentNotes, setAppointmentNotes] = useState<AppointmentNote[]>([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'notes'>('details');
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [appointmentPhotoUrls, setAppointmentPhotoUrls] = useState<Map<string, string>>(new Map());
  
  // Organization/Team state
  const [teamMembers, setTeamMembers] = useState<OrganizationMember[]>([]);
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  // Offline/sync state
  const { status: networkStatus } = useNetworkStatus();
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isSyncing: false,
    lastSyncedAt: null,
    pendingCount: 0,
    errors: []
  });
  const syncService = useRef(CalendarSyncService.getInstance());

  const [newAppointment, setNewAppointment] = useState<NewAppointment>({
    appointment_type: '',
    date: '',
    time: '',
    address: '',
    client_name: '',
    phone_number: '',
    status: 'to_be_completed'
  });

  // Load appointments and types on mount
  useEffect(() => {
    loadData();
    loadTeamMembers();
    
    // Initialize sync service listener
    const handleSyncStatusChange = (status: SyncStatus) => {
      setSyncStatus(status);
      if (!status.isSyncing && status.pendingCount === 0 && status.errors.length === 0) {
        loadData();
      }
    };
    
    syncService.current.addListener(handleSyncStatusChange);
    
    return () => {
      syncService.current.removeListener(handleSyncStatusChange);
    };
  }, []);

  // Reload appointments when viewing user changes
  useEffect(() => {
    if (!loading) {
      loadData();
    }
  }, [viewingUserId]);

  // Load photo URLs for appointments
  useEffect(() => {
    const loadPhotoUrls = async () => {
      const newUrls = new Map<string, string>();
      for (const apt of appointments) {
        if (apt.photo_url && !appointmentPhotoUrls.has(apt.id)) {
          try {
            const url = await getAppointmentPhotoUrl(apt.photo_url);
            newUrls.set(apt.id, url);
          } catch (error) {
            console.error(`Error loading photo for appointment ${apt.id}:`, error);
          }
        }
      }
      if (newUrls.size > 0) {
        setAppointmentPhotoUrls(prev => new Map([...prev, ...newUrls]));
      }
    };
    
    if (appointments.length > 0) {
      loadPhotoUrls();
    }
  }, [appointments]);

  const loadTeamMembers = async () => {
    try {
      const org = await getUserOrganization();
      if (org) {
        const members = await getOrganizationMembers();
        setTeamMembers(members);
        
        // Find current user's role
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setCurrentUserId(user.id);
          const currentMember = members.find(m => m.user_id === user.id);
          if (currentMember) {
            setCurrentUserRole(currentMember.role);
            setIsOrgAdmin(currentMember.role === 'team_admin' || currentMember.role === 'super_admin');
          }
        }
      }
    } catch (err) {
      console.error('Error loading team members:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await initializeDefaultAppointmentTypes();
      const [appointmentsData, typesData] = await Promise.all([
        fetchAppointments(viewingUserId || undefined),
        fetchAppointmentTypes()
      ]);
      setAppointments(appointmentsData);
      setAppointmentTypes(typesData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  // Get current month and year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Get first day of month and number of days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDay = firstDayOfMonth.getDay();

  // Month names
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const getAppointmentsForDate = (date: string) => {
    return appointments.filter(apt => apt.date === date);
  };

  const formatDateString = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const handleAddAppointment = async () => {
    if (!newAppointment.date || !newAppointment.time || !newAppointment.appointment_type) return;
    
    try {
      setSaving(true);
      setError(null);

      let photoUrl: string | undefined;
      if (selectedPhoto) {
        photoUrl = await uploadAppointmentPhoto(selectedPhoto);
      }

      if (editingAppointment) {
        // Update existing appointment
        const updated = await updateAppointment(editingAppointment.id, {
          ...newAppointment,
          photo_url: photoUrl || editingAppointment.photo_url
        });
        setAppointments(appointments.map(apt => apt.id === updated.id ? updated : apt));
      } else {
        // Create new appointment
        const created = await createAppointment({
          ...newAppointment,
          photo_url: photoUrl
        });
        setAppointments([...appointments, created]);
        
        // Save any temporary notes that were added during creation
        if (appointmentNotes.length > 0 && created.id) {
          for (const note of appointmentNotes) {
            if (note.id.startsWith('temp-')) {
              try {
                await createAppointmentNote(created.id, note.note_text);
              } catch (noteErr) {
                console.error('Error saving note:', noteErr);
              }
            }
          }
        }
      }

      resetForm();
      setShowAddModal(false);
    } catch (err) {
      console.error('Error saving appointment:', err);
      setError('Failed to save appointment');
    } finally {
      setSaving(false);
    }
  };

  const handleEditAppointment = async (apt: Appointment) => {
    setEditingAppointment(apt);
    setNewAppointment({
      appointment_type: apt.appointment_type,
      date: apt.date,
      time: apt.time,
      address: apt.address || '',
      client_name: apt.client_name || '',
      phone_number: apt.phone_number || '',
      status: apt.status,
      photo_url: apt.photo_url
    });
    
    // Load notes for this appointment
    if (apt.id) {
      setLoadingNotes(true);
      try {
        const notes = await fetchAppointmentNotes(apt.id);
        setAppointmentNotes(notes);
      } catch (err) {
        console.error('Error loading notes:', err);
      } finally {
        setLoadingNotes(false);
      }
    }
    if (apt.photo_url) {
      getAppointmentPhotoUrl(apt.photo_url).then(url => {
        setPhotoPreview(url);
      }).catch(err => {
        console.error('Error loading photo preview:', err);
      });
    }
    setShowAddModal(true);
  };

  const handleDeleteAppointment = async (id: string) => {
    if (!confirm('Are you sure you want to delete this appointment?')) return;
    
    try {
      const appointment = appointments.find(a => a.id === id);
      if (appointment?.photo_url) {
        await deleteAppointmentPhoto(appointment.photo_url);
      }
      await deleteAppointment(id);
      setAppointments(appointments.filter(apt => apt.id !== id));
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError('Failed to delete appointment');
    }
  };

  const handleAddCustomType = async () => {
    if (!newTypeName.trim()) return;
    
    try {
      const newType = await addAppointmentType(newTypeName.trim());
      setAppointmentTypes([...appointmentTypes, newType]);
      setNewAppointment({...newAppointment, appointment_type: newType.type_name});
      setNewTypeName('');
      setShowAddTypeInput(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add appointment type');
    }
  };

  const handleDeleteCustomType = async (typeId: string, typeName: string) => {
    if (!confirm(`Delete "${typeName}" from your appointment types?`)) return;
    
    try {
      await deleteAppointmentType(typeId);
      setAppointmentTypes(appointmentTypes.filter(t => t.id !== typeId));
    } catch (err) {
      console.error('Error deleting appointment type:', err);
      setError('Failed to delete appointment type');
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePostcodeSearch = async (postcode: string) => {
    setPostcodeSearch(postcode);
    
    if (postcode.length < 3) {
      setAddressResults([]);
      return;
    }
    
    try {
      setSearchingAddress(true);
      const results = await searchAddressesByPostcode(postcode);
      setAddressResults(results);
    } catch (err) {
      console.error('Error searching addresses:', err);
      setAddressResults([]);
    } finally {
      setSearchingAddress(false);
    }
  };

  const handleSelectAddress = (address: UKAddress) => {
    const formattedAddress = formatAddressForDisplay(address);
    setNewAppointment({...newAppointment, address: formattedAddress});
    setPostcodeSearch('');
    setAddressResults([]);
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) return;
    
    // If editing existing appointment, save immediately
    if (editingAppointment?.id) {
      try {
        const newNote = await createAppointmentNote(editingAppointment.id, newNoteText.trim());
        setAppointmentNotes([newNote, ...appointmentNotes]);
        setNewNoteText('');
      } catch (err) {
        console.error('Error adding note:', err);
        setError('Failed to add note');
      }
    } else {
      // For new appointments, store notes temporarily
      const tempNote: AppointmentNote = {
        id: `temp-${Date.now()}`,
        appointment_id: '',
        user_id: '',
        note_text: newNoteText.trim(),
        created_at: new Date().toISOString()
      };
      setAppointmentNotes([tempNote, ...appointmentNotes]);
      setNewNoteText('');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Delete this note?')) return;
    
    try {
      await deleteAppointmentNote(noteId);
      setAppointmentNotes(appointmentNotes.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Failed to delete note');
    }
  };

  const handleViewAppointment = async (apt: Appointment) => {
    setViewingAppointment(apt);
    
    // Load notes for this appointment
    if (apt.id) {
      setLoadingNotes(true);
      try {
        const notes = await fetchAppointmentNotes(apt.id);
        setAppointmentNotes(notes);
      } catch (err) {
        console.error('Error loading notes:', err);
      } finally {
        setLoadingNotes(false);
      }
    }
    
    setShowViewModal(true);
  };

  const openDirections = (address: string) => {
    setDirectionsAddress(address);
    setShowDirectionsModal(true);
  };

  const resetForm = () => {
    setNewAppointment({
      appointment_type: '',
      date: '',
      time: '',
      address: '',
      client_name: '',
      phone_number: '',
      status: 'to_be_completed'
    });
    setEditingAppointment(null);
    setSelectedPhoto(null);
    setPhotoPreview(null);
    setShowAddTypeInput(false);
    setNewTypeName('');
    setPostcodeSearch('');
    setAddressResults([]);
    setAppointmentNotes([]);
    setNewNoteText('');
    setActiveTab('details');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusColor = (status: AppointmentStatus) => {
    switch (status) {
      case 'to_be_completed': return 'bg-blue-500';
      case 'attended': return 'bg-green-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-slate-500';
    }
  };

  const getStatusIcon = (status: AppointmentStatus) => {
    switch (status) {
      case 'to_be_completed': return <Clock className="w-3 h-3" />;
      case 'attended': return <CheckCircle className="w-3 h-3" />;
      case 'cancelled': return <XCircle className="w-3 h-3" />;
      default: return <AlertCircle className="w-3 h-3" />;
    }
  };

  const getStatusLabel = (status: AppointmentStatus) => {
    switch (status) {
      case 'to_be_completed': return 'To Be Completed';
      case 'attended': return 'Attended';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const renderCalendarDays = () => {
    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = formatDateString(currentYear, currentMonth, day);
      const dayAppointments = getAppointmentsForDate(dateString);
      const isToday = new Date().toDateString() === new Date(currentYear, currentMonth, day).toDateString();
      const isSelected = selectedDate === dateString;
      
      days.push(
        <div
          key={day}
          onClick={() => setSelectedDate(dateString)}
          className={`p-2 min-h-[80px] border border-slate-200 cursor-pointer transition-all duration-200 hover:bg-blue-50 ${
            isToday ? 'bg-blue-100 border-blue-300' : ''
          } ${isSelected ? 'bg-blue-200 border-blue-400' : ''}`}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-700' : 'text-slate-700'}`}>
            {day}
          </div>
          <div className="space-y-1">
            {dayAppointments.slice(0, 2).map(apt => (
              <div
                key={apt.id}
                className={`text-xs px-1.5 py-1 rounded text-white truncate ${getStatusColor(apt.status)}`}
              >
                <div className="font-medium">{formatTime(apt.time)}</div>
                <div className="text-[10px] leading-tight">{apt.address || apt.client_name || apt.appointment_type}</div>
              </div>
            ))}
            {dayAppointments.length > 2 && (
              <div className="text-xs text-slate-500">+{dayAppointments.length - 2} more</div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 shadow-2xl sticky top-0 z-40">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-white/10 to-indigo-600/0"></div>
        <div className="relative w-full px-4 sm:px-6 flex items-center justify-between app-header-padding">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              <CalendarIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                Calendar
              </h1>
              <p className="text-blue-100 text-xs sm:text-sm hidden sm:block">Schedule appointments and assessments</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Team Member Selector (for admins/superadmins) */}
            {isOrgAdmin && teamMembers.length > 0 && (
              <select
                value={viewingUserId || ''}
                onChange={(e) => setViewingUserId(e.target.value || null)}
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm rounded-xl text-white bg-white/20 border border-white/30 hover:bg-white/30 transition-all duration-200 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white/50 max-w-[140px]"
              >
                <option value="" className="bg-slate-700 text-white">My Calendar</option>
                {teamMembers
                  .filter(member => member.user_id !== currentUserId)
                  .map(member => (
                    <option key={member.user_id} value={member.user_id} className="bg-slate-700 text-white">
                      {formatMemberName(member)}
                    </option>
                  ))}
              </select>
            )}
            
            <button
              onClick={() => setShowAddModal(true)}
              className="p-2 sm:p-2.5 rounded-xl text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200 backdrop-blur-sm border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl"
            >
              <Plus size={16} className="sm:w-[18px] sm:h-[18px]" />
            </button>
            <button
              onClick={() => onNavigate({ page: 'start' })}
              title="Go to Home"
              className="p-2 sm:p-2.5 rounded-xl text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200 backdrop-blur-sm border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl"
            >
              <Home size={16} className="sm:w-[18px] sm:h-[18px]" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto p-4 sm:p-6 lg:p-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200/50 overflow-hidden">
          {/* Calendar Header */}
          <div className="relative bg-gradient-to-r from-slate-50 to-blue-50/30 px-6 py-4 border-b border-slate-200/50">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-blue-700 bg-clip-text text-transparent">
                {monthNames[currentMonth]} {currentYear}
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrevMonth}
                  className="p-2 rounded-lg hover:bg-white/50 transition-colors duration-200"
                >
                  <ChevronDown className="w-5 h-5 rotate-90 text-slate-600" />
                </button>
                <button
                  onClick={handleNextMonth}
                  className="p-2 rounded-lg hover:bg-white/50 transition-colors duration-200"
                >
                  <ChevronDown className="w-5 h-5 -rotate-90 text-slate-600" />
                </button>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="p-2 sm:p-4 md:p-6">
            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-0 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-1 sm:p-2 md:p-3 text-center text-xs sm:text-sm font-semibold text-slate-600 bg-slate-100/50">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-0 border border-slate-200">
              {renderCalendarDays()}
            </div>
          </div>
        </div>
        )}

        {/* Selected Date Details */}
        {selectedDate && (
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl mt-6 border border-slate-200/50 overflow-hidden">
            <div className="relative bg-gradient-to-r from-slate-50 to-blue-50/30 px-6 py-4 border-b border-slate-200/50">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              <div className="relative">
                <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-blue-700 bg-clip-text text-transparent">
                  Appointments for {new Date(selectedDate).toLocaleDateString()}
                </h3>
              </div>
            </div>
            
            <div className="p-6">
              {getAppointmentsForDate(selectedDate).length === 0 ? (
                <p className="text-slate-500 text-center py-8">No appointments scheduled for this date</p>
              ) : (
                <div className="space-y-4">
                  {getAppointmentsForDate(selectedDate).map(apt => (
                    <div 
                      key={apt.id} 
                      onClick={() => handleViewAppointment(apt)}
                      className="bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-xl p-4 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all duration-200"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className={`p-2 rounded-lg ${getStatusColor(apt.status)} bg-opacity-20`}>
                            {getStatusIcon(apt.status)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-semibold text-slate-800">{apt.address || 'No address'}</h4>
                              <span className={`px-2 py-0.5 text-xs font-medium text-white rounded-full ${getStatusColor(apt.status)}`}>
                                {getStatusLabel(apt.status)}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">{apt.appointment_type}</p>
                            <div className="flex items-center flex-wrap gap-4 text-sm text-slate-600 mt-2">
                              <div className="flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>{formatTime(apt.time)}</span>
                              </div>
                              {apt.address && (
                                <div className="flex items-center space-x-1">
                                  <MapPin className="w-3 h-3" />
                                  <span>{apt.address}</span>
                                </div>
                              )}
                            </div>
                            {apt.notes && (
                              <p className="text-sm text-slate-500 mt-2">{apt.notes}</p>
                            )}
                            {apt.photo_url && appointmentPhotoUrls.get(apt.id) && (
                              <img 
                                src={appointmentPhotoUrls.get(apt.id)} 
                                alt="Appointment" 
                                className="mt-2 rounded-lg w-32 h-32 object-cover border border-slate-200"
                              />
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditAppointment(apt)}
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit3 className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteAppointment(apt.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Add Appointment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200/50 max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="relative bg-gradient-to-r from-slate-50 to-blue-50/30 px-6 py-5 border-b border-slate-200/50">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-blue-700 bg-clip-text text-transparent">
                    {editingAppointment ? 'Edit Appointment' : 'Add Appointment'}
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-white/50 rounded-xl transition-all duration-200 group"
                >
                  <X className="w-5 h-5 text-slate-500 group-hover:text-slate-700" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-200">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`flex-1 px-4 py-3 text-sm font-semibold transition-all duration-200 ${
                    activeTab === 'details'
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-2" />
                  Details
                </button>
                <button
                  onClick={() => setActiveTab('notes')}
                  className={`flex-1 px-4 py-3 text-sm font-semibold transition-all duration-200 ${
                    activeTab === 'notes'
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
                  }`}
                >
                  <StickyNote className="w-4 h-4 inline mr-2" />
                  Notes ({appointmentNotes.length})
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
              {activeTab === 'details' ? (
                <>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Appointment Type</label>
                <select
                  value={newAppointment.appointment_type}
                  onChange={(e) => {
                    if (e.target.value === '__add_new__') {
                      setShowAddTypeInput(true);
                    } else {
                      setNewAppointment({...newAppointment, appointment_type: e.target.value});
                    }
                  }}
                  className="w-full px-4 py-3 bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400 transition-all duration-200"
                >
                  <option value="">Select type...</option>
                  {appointmentTypes.map(type => (
                    <option key={type.id} value={type.type_name}>
                      {type.type_name}
                    </option>
                  ))}
                  <option value="__add_new__">+ Add Custom Type</option>
                </select>
                
                {showAddTypeInput && (
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      type="text"
                      value={newTypeName}
                      onChange={(e) => setNewTypeName(e.target.value)}
                      placeholder="Enter new type name"
                      className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddCustomType()}
                    />
                    <button
                      onClick={handleAddCustomType}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 text-sm font-medium"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowAddTypeInput(false);
                        setNewTypeName('');
                      }}
                      className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                
                {/* Show custom types with delete option */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {appointmentTypes.filter(t => !t.is_default).map(type => (
                    <div key={type.id} className="inline-flex items-center space-x-1 px-3 py-1 bg-slate-100 rounded-full text-xs">
                      <span className="text-slate-700">{type.type_name}</span>
                      <button
                        onClick={() => handleDeleteCustomType(type.id, type.type_name)}
                        className="text-slate-400 hover:text-red-600 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Assign To (for admins/superadmins) */}
              {isOrgAdmin && teamMembers.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">Assign To</label>
                  <select
                    value={newAppointment.assigned_to_user_id || ''}
                    onChange={(e) => setNewAppointment({...newAppointment, assigned_to_user_id: e.target.value || undefined})}
                    className="w-full px-4 py-3 bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400 transition-all duration-200"
                  >
                    <option value="">Me</option>
                    {teamMembers
                      .filter(member => member.user_id !== currentUserId)
                      .map(member => (
                        <option key={member.user_id} value={member.user_id}>
                          {formatMemberName(member)}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Status</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setNewAppointment({...newAppointment, status: 'to_be_completed'})}
                    className={`px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
                      newAppointment.status === 'to_be_completed'
                        ? 'bg-blue-500 text-white shadow-lg transform scale-[1.02]'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    <span>To Do</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewAppointment({...newAppointment, status: 'attended'})}
                    className={`px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
                      newAppointment.status === 'attended'
                        ? 'bg-green-500 text-white shadow-lg transform scale-[1.02]'
                        : 'bg-green-50 text-green-700 hover:bg-green-100'
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>Done</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewAppointment({...newAppointment, status: 'cancelled'})}
                    className={`px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
                      newAppointment.status === 'cancelled'
                        ? 'bg-red-500 text-white shadow-lg transform scale-[1.02]'
                        : 'bg-red-50 text-red-700 hover:bg-red-100'
                    }`}
                  >
                    <XCircle className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">Date</label>
                  <input
                    type="date"
                    value={newAppointment.date}
                    onChange={(e) => setNewAppointment({...newAppointment, date: e.target.value})}
                    className="w-full px-4 py-3 bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400 transition-all duration-200"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">Time</label>
                  <input
                    type="time"
                    step="60"
                    value={newAppointment.time}
                    onChange={(e) => setNewAppointment({...newAppointment, time: e.target.value})}
                    className="w-full px-4 py-3 bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400 transition-all duration-200"
                  />
                </div>
              </div>

              {/* Postcode Search */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Search Address by Postcode</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={postcodeSearch}
                    onChange={(e) => handlePostcodeSearch(e.target.value)}
                    placeholder="Enter postcode (e.g., SW1A 1AA)"
                    className="w-full pl-10 pr-4 py-3 bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400 transition-all duration-200 placeholder-slate-400"
                  />
                </div>
                {searchingAddress && (
                  <p className="text-xs text-slate-500 flex items-center space-x-1">
                    <span className="animate-spin">⏳</span>
                    <span>Searching...</span>
                  </p>
                )}
                {addressResults.length > 0 && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg bg-white shadow-lg">
                    {addressResults.map((addr, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSelectAddress(addr)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-0"
                      >
                        {formatAddressForDisplay(addr)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Address Field */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Property Address</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={newAppointment.address}
                    onChange={(e) => setNewAppointment({...newAppointment, address: e.target.value})}
                    placeholder="Property address"
                    className="w-full pl-10 pr-4 py-3 bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400 transition-all duration-200 placeholder-slate-400"
                  />
                </div>
              </div>

              {/* Client Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">Client Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={newAppointment.client_name}
                      onChange={(e) => setNewAppointment({...newAppointment, client_name: e.target.value})}
                      placeholder="Client name"
                      className="w-full pl-10 pr-4 py-3 bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400 transition-all duration-200 placeholder-slate-400"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="tel"
                      value={newAppointment.phone_number}
                      onChange={(e) => setNewAppointment({...newAppointment, phone_number: e.target.value})}
                      placeholder="Phone number"
                      className="w-full pl-10 pr-4 py-3 bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400 transition-all duration-200 placeholder-slate-400"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Photo <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                {!photoPreview ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full px-4 py-8 bg-gradient-to-r from-slate-50 to-blue-50/30 border-2 border-dashed border-slate-300 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all duration-200 flex flex-col items-center space-y-2 group"
                  >
                    <Camera className="w-8 h-8 text-slate-400 group-hover:text-blue-500 transition-colors" />
                    <span className="text-sm text-slate-600 group-hover:text-blue-600 font-medium">Click to add photo</span>
                  </button>
                ) : (
                  <div className="relative">
                    <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover rounded-xl border border-slate-200" />
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  disabled={saving}
                  className="px-6 py-3 text-sm font-semibold text-slate-700 bg-gradient-to-r from-white to-slate-50 border border-slate-200 rounded-xl hover:from-slate-50 hover:to-slate-100 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAppointment}
                  disabled={!newAppointment.date || !newAppointment.time || !newAppointment.appointment_type || saving}
                  className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {saving ? 'Saving...' : (editingAppointment ? 'Update Appointment' : 'Add Appointment')}
                </button>
              </div>
              </>
              ) : (
                /* Notes Tab */
                <div className="space-y-4">
                  {loadingNotes ? (
                    <p className="text-center text-slate-500 py-8">Loading notes...</p>
                  ) : (
                    <>
                      {/* Add Note Input */}
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-slate-700">Add New Note</label>
                        <div className="flex space-x-2">
                          <textarea
                            value={newNoteText}
                            onChange={(e) => setNewNoteText(e.target.value)}
                            placeholder="Enter note..."
                            rows={3}
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400 transition-all duration-200 placeholder-slate-400 resize-none"
                          />
                        </div>
                        <button
                          onClick={handleAddNote}
                          disabled={!newNoteText.trim()}
                          className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Plus className="w-4 h-4 inline mr-1" />
                          Add Note
                        </button>
                      </div>

                      {/* Notes List */}
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-slate-700">Previous Notes</h3>
                        {appointmentNotes.length === 0 ? (
                          <p className="text-sm text-slate-500 text-center py-8 bg-slate-50 rounded-lg">No notes yet. Add your first note above.</p>
                        ) : (
                          <div className="space-y-2">
                            {appointmentNotes.map(note => (
                              <div key={note.id} className="p-4 bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-lg">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.note_text}</p>
                                    <p className="text-xs text-slate-500 mt-2">
                                      {new Date(note.created_at!).toLocaleString()}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteNote(note.id)}
                                    className="ml-2 p-1 hover:bg-red-50 rounded transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Close button for notes tab */}
                      <div className="flex justify-end pt-2">
                        <button
                          onClick={() => {
                            setShowAddModal(false);
                            resetForm();
                          }}
                          className="px-6 py-3 text-sm font-semibold text-slate-700 bg-gradient-to-r from-white to-slate-50 border border-slate-200 rounded-xl hover:from-slate-50 hover:to-slate-100 transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          Close
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Appointment Modal (Read-Only) */}
      {showViewModal && viewingAppointment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200/50 max-w-lg w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="relative bg-gradient-to-r from-slate-50 to-blue-50/30 px-6 py-5 border-b border-slate-200/50">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`p-2.5 rounded-xl shadow-lg ${getStatusColor(viewingAppointment.status)}`}>
                    {getStatusIcon(viewingAppointment.status)}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-blue-700 bg-clip-text text-transparent">
                      {viewingAppointment.address || 'Appointment Details'}
                    </h2>
                    <p className="text-sm text-slate-600">{viewingAppointment.appointment_type}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingAppointment(null);
                    setAppointmentNotes([]);
                  }}
                  className="p-2 hover:bg-white/50 rounded-xl transition-all duration-200 group"
                >
                  <X className="w-5 h-5 text-slate-500 group-hover:text-slate-700" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Status Badge */}
              <div className="flex items-center justify-center">
                <span className={`px-4 py-2 text-sm font-medium text-white rounded-full ${getStatusColor(viewingAppointment.status)}`}>
                  {getStatusLabel(viewingAppointment.status)}
                </span>
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <p className="text-xs font-semibold text-blue-600 mb-1">Date</p>
                  <p className="text-sm font-medium text-slate-800">{new Date(viewingAppointment.date).toLocaleDateString()}</p>
                </div>
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <p className="text-xs font-semibold text-blue-600 mb-1">Time</p>
                  <p className="text-sm font-medium text-slate-800">{formatTime(viewingAppointment.time)}</p>
                </div>
              </div>

              {/* Address with Directions */}
              {viewingAppointment.address && (
                <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50/30 rounded-xl border border-slate-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-slate-600 mb-1">Address</p>
                      <p className="text-sm font-medium text-slate-800">{viewingAppointment.address}</p>
                    </div>
                    <button
                      onClick={() => openDirections(viewingAppointment.address!)}
                      className="ml-3 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center space-x-2 text-sm font-semibold"
                    >
                      <MapPin className="w-4 h-4" />
                      <span>Directions</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Client Details */}
              {(viewingAppointment.client_name || viewingAppointment.phone_number) && (
                <div className="grid grid-cols-2 gap-4">
                  {viewingAppointment.client_name && (
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl border border-purple-100">
                      <p className="text-xs font-semibold text-purple-600 mb-1">Client Name</p>
                      <p className="text-sm font-medium text-slate-800">{viewingAppointment.client_name}</p>
                    </div>
                  )}
                  {viewingAppointment.phone_number && (
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl border border-purple-100">
                      <p className="text-xs font-semibold text-purple-600 mb-1">Phone</p>
                      <p className="text-sm font-medium text-slate-800">{viewingAppointment.phone_number}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Photo */}
              {viewingAppointment.photo_url && appointmentPhotoUrls.get(viewingAppointment.id) && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-600">Photo</p>
                  <img 
                    src={appointmentPhotoUrls.get(viewingAppointment.id)} 
                    alt="Appointment" 
                    className="w-full h-64 object-cover rounded-xl border border-slate-200 shadow-md"
                  />
                </div>
              )}

              {/* Notes */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700">Notes</h3>
                {loadingNotes ? (
                  <p className="text-center text-slate-500 py-4">Loading notes...</p>
                ) : appointmentNotes.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8 bg-slate-50 rounded-lg">No notes for this appointment.</p>
                ) : (
                  <div className="space-y-2">
                    {appointmentNotes.map(note => (
                      <div key={note.id} className="p-4 bg-gradient-to-r from-white to-slate-50/50 border border-slate-200 rounded-lg">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.note_text}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          {new Date(note.created_at!).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-6 border-t border-slate-200 flex justify-between">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingAppointment(null);
                  setAppointmentNotes([]);
                  handleEditAppointment(viewingAppointment);
                }}
                className="px-6 py-3 text-sm font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-all duration-200"
              >
                <Edit3 className="w-4 h-4 inline mr-2" />
                Edit
              </button>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingAppointment(null);
                  setAppointmentNotes([]);
                }}
                className="px-6 py-3 text-sm font-semibold text-slate-700 bg-gradient-to-r from-white to-slate-50 border border-slate-200 rounded-xl hover:from-slate-50 hover:to-slate-100 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Directions Modal */}
      {showDirectionsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Get Directions</h2>
                <button
                  onClick={() => setShowDirectionsModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-green-100 mt-2">{directionsAddress}</p>
            </div>
            
            <div className="p-6 space-y-3">
              <button
                onClick={() => {
                  const encoded = encodeURIComponent(directionsAddress);
                  window.location.href = `maps://maps.apple.com/?q=${encoded}`;
                  setShowDirectionsModal(false);
                }}
                className="w-full px-6 py-4 bg-gradient-to-r from-slate-700 to-slate-900 text-white rounded-xl hover:from-slate-800 hover:to-black transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-between group"
              >
                <span className="font-semibold">Apple Maps</span>
                <MapPin className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </button>
              
              <button
                onClick={() => {
                  const encoded = encodeURIComponent(directionsAddress);
                  window.open(`https://maps.google.com/?q=${encoded}`, '_blank');
                  setShowDirectionsModal(false);
                }}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl hover:from-blue-600 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-between group"
              >
                <span className="font-semibold">Google Maps</span>
                <MapPin className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </button>
              
              <button
                onClick={() => {
                  const encoded = encodeURIComponent(directionsAddress);
                  window.open(`https://waze.com/ul?q=${encoded}`, '_blank');
                  setShowDirectionsModal(false);
                }}
                className="w-full px-6 py-4 bg-gradient-to-r from-cyan-400 to-cyan-600 text-white rounded-xl hover:from-cyan-500 hover:to-cyan-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-between group"
              >
                <span className="font-semibold">Waze</span>
                <MapPin className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
